// Shared UI primitives: icons, engraved labels, headers, bottom nav.
// Ported from the prototype's ui.jsx.
import type { CSSProperties, ReactNode } from "react";

export type IconName =
  | "camera"
  | "upload"
  | "grid"
  | "face"
  | "search"
  | "heart"
  | "chevron"
  | "back"
  | "close"
  | "check"
  | "download"
  | "share"
  | "sparkle"
  | "home"
  | "plus"
  | "refresh"
  | "bolt"
  | "qr";

const ICONS: Record<IconName, ReactNode> = {
  camera: (
    <>
      <path d="M3 8.5A2 2 0 0 1 5 6.5h1.4l1-1.6A1 1 0 0 1 9.2 4.4h5.6a1 1 0 0 1 .84.5l1 1.6H18a2 2 0 0 1 2 2V18a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2Z" />
      <circle cx="12" cy="13" r="3.4" />
    </>
  ),
  upload: (
    <>
      <path d="M12 15.5V5" />
      <path d="m7.5 9 4.5-4.5L16.5 9" />
      <path d="M5 15v2.5a2 2 0 0 0 2 2h10a2 2 0 0 0 2-2V15" />
    </>
  ),
  grid: (
    <>
      <rect x="4" y="4" width="7" height="7" rx="1.2" />
      <rect x="13" y="4" width="7" height="7" rx="1.2" />
      <rect x="4" y="13" width="7" height="7" rx="1.2" />
      <rect x="13" y="13" width="7" height="7" rx="1.2" />
    </>
  ),
  face: (
    <>
      <circle cx="12" cy="12" r="8.4" />
      <circle cx="9.3" cy="10.6" r="0.9" fill="currentColor" stroke="none" />
      <circle cx="14.7" cy="10.6" r="0.9" fill="currentColor" stroke="none" />
      <path d="M9 14.6c.9.9 1.9 1.3 3 1.3s2.1-.4 3-1.3" />
    </>
  ),
  search: (
    <>
      <circle cx="11" cy="11" r="6" />
      <path d="m20 20-4.3-4.3" />
    </>
  ),
  heart: (
    <path d="M12 20s-6.8-4.3-9-8.2C1.4 8.9 2.6 5.6 5.8 5.2 8 4.9 9.3 6 12 8.6 14.7 6 16 4.9 18.2 5.2c3.2.4 4.4 3.7 2.8 6.6C18.8 15.7 12 20 12 20Z" />
  ),
  chevron: <path d="m9 5 7 7-7 7" />,
  back: <path d="m15 5-7 7 7 7" />,
  close: (
    <>
      <path d="M6 6 18 18" />
      <path d="M18 6 6 18" />
    </>
  ),
  check: <path d="m5 12.5 4.5 4.5L19 7" />,
  download: (
    <>
      <path d="M12 4v11" />
      <path d="m7.5 10.5 4.5 4.5 4.5-4.5" />
      <path d="M5 19h14" />
    </>
  ),
  share: (
    <>
      <circle cx="6" cy="12" r="2.4" />
      <circle cx="17" cy="6" r="2.4" />
      <circle cx="17" cy="18" r="2.4" />
      <path d="m8.1 10.9 6.8-3.8M8.1 13.1l6.8 3.8" />
    </>
  ),
  sparkle: (
    <>
      <path d="M12 4c.4 3.2 1.4 4.2 4.6 4.6-3.2.4-4.2 1.4-4.6 4.6-.4-3.2-1.4-4.2-4.6-4.6C10.6 8.2 11.6 7.2 12 4Z" />
      <path d="M18 14c.2 1.6.7 2.1 2.3 2.3-1.6.2-2.1.7-2.3 2.3-.2-1.6-.7-2.1-2.3-2.3 1.6-.2 2.1-.7 2.3-2.3Z" />
    </>
  ),
  home: (
    <>
      <path d="M4 11 12 4l8 7" />
      <path d="M6 9.5V19a1 1 0 0 0 1 1h10a1 1 0 0 0 1-1V9.5" />
    </>
  ),
  plus: (
    <>
      <path d="M12 5v14" />
      <path d="M5 12h14" />
    </>
  ),
  refresh: (
    <>
      <path d="M19 12a7 7 0 1 1-2-4.9" />
      <path d="M19 4v3.5h-3.5" />
    </>
  ),
  bolt: <path d="M12 3 6 13h5l-1 8 7-11h-5Z" />,
  qr: (
    <>
      <rect x="4" y="4" width="6" height="6" rx="1" />
      <rect x="14" y="4" width="6" height="6" rx="1" />
      <rect x="4" y="14" width="6" height="6" rx="1" />
      <path d="M14 14h2v2M20 14v2M14 18v2h2M18 18h2v2" />
    </>
  ),
};

interface IconProps {
  name: IconName;
  size?: number;
  stroke?: number;
  fill?: string;
  className?: string;
  style?: CSSProperties;
}

export function Icon({ name, size = 22, stroke = 1.6, fill = "none", className = "", style = {} }: IconProps) {
  return (
    <svg
      className={className}
      width={size}
      height={size}
      viewBox="0 0 24 24"
      fill={fill}
      stroke="currentColor"
      strokeWidth={stroke}
      strokeLinecap="round"
      strokeLinejoin="round"
      style={style}
      aria-hidden="true"
    >
      {ICONS[name]}
    </svg>
  );
}

// engraved spaced small-caps label (the dusty-blue invite type)
export function Engraved({
  children,
  size = 13,
  className = "",
  style = {},
  as: Tag = "div",
}: {
  children: ReactNode;
  size?: number;
  className?: string;
  style?: CSSProperties;
  as?: "div" | "span" | "p";
}) {
  return (
    <Tag className={"engraved " + className} style={{ fontSize: size, ...style }}>
      {children}
    </Tag>
  );
}

export function Script({
  children,
  size = 44,
  className = "",
  style = {},
}: {
  children: ReactNode;
  size?: number;
  className?: string;
  style?: CSSProperties;
}) {
  return (
    <div className={"script " + className} style={{ fontSize: size, ...style }}>
      {children}
    </div>
  );
}

// top app bar for inner screens
export function TopBar({ title, onBack, right }: { title: ReactNode; onBack?: () => void; right?: ReactNode }) {
  return (
    <div className="topbar">
      {onBack ? (
        <button className="iconbtn" onClick={onBack} aria-label="Back">
          <Icon name="back" size={22} />
        </button>
      ) : (
        <span style={{ width: 40 }} />
      )}
      <Engraved size={13} className="topbar-title">
        {title}
      </Engraved>
      <div className="topbar-right">{right || <span style={{ width: 40 }} />}</div>
    </div>
  );
}

export type ScreenId = "home" | "upload" | "findme" | "moments" | "moment" | "search" | "all";

export function BottomNav({
  current,
  go,
  onUpload,
  showFindMe = true,
  showUpload = true,
}: {
  current: ScreenId;
  go: (s: ScreenId) => void;
  onUpload: () => void;
  showFindMe?: boolean;
  showUpload?: boolean;
}) {
  const item = (id: ScreenId, name: IconName, label: string) => (
    <button className={"navitem" + (current === id ? " active" : "")} onClick={() => go(id)}>
      <Icon name={name} size={23} stroke={current === id ? 1.9 : 1.5} />
      <span>{label}</span>
    </button>
  );
  return (
    <nav className="bottomnav">
      {item("home", "home", "Home")}
      {item("moments", "grid", "Moments")}
      {showUpload ? (
        <button className="navfab" onClick={onUpload} aria-label="Add photos">
          <Icon name="plus" size={26} stroke={2.1} />
        </button>
      ) : (
        <span aria-hidden="true" />
      )}
      {showFindMe && item("findme", "face", "Find Me")}
      <button className={"navitem" + (current === "search" ? " active" : "")} onClick={() => go("search")}>
        <Icon name="search" size={22} stroke={current === "search" ? 1.9 : 1.5} />
        <span>Search</span>
      </button>
    </nav>
  );
}
